import { useMemo, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';

import { QUERY_KEYS } from 'constants/queryKeys';
import { subscriptionsServices } from '@services';
import { showError, getCleanedUrlParams } from '@utils';

import {
  CONTRACTS_PAGE_SIZE,
  CONTRACTS_DEFAULT_ORDER,
  CONTRACTS_DEFAULT_ORDER_BY,
} from 'components/subscriptionContracts/constants';
import { handleContracts, getSubscriptionContractsPayload } from './utils';

export const useSubscriptionContracts = () => {
  const { query, isReady } = useRouter();

  const {
    page = 1,
    pageSize = CONTRACTS_PAGE_SIZE,
    order = CONTRACTS_DEFAULT_ORDER,
    orderBy = CONTRACTS_DEFAULT_ORDER_BY,
    dateFrom,
    dateTo,
    query: searchQuery = '',
    ...rest
  } = query;

  const getContractsParamsAndPayload = () => {
    const params = {
      page,
      pageSize: parseInt(pageSize, 10) || CONTRACTS_PAGE_SIZE,
      order,
      orderBy,
      query: searchQuery,
    };

    const cleanedParams = getCleanedUrlParams(params);

    const payload = getSubscriptionContractsPayload(
      getCleanedUrlParams({
        dateFrom,
        dateTo,
        query: searchQuery,
        ...rest,
      })
    );
    return {
      params: cleanedParams,
      payload,
    };
  };

  const { data: enumsData } = useQuery(
    [QUERY_KEYS.GET_CONTRACTS_ENUMS_KEY],
    () => subscriptionsServices.getSubscriptionContractsEnums()
  );

  const { params, payload } = getContractsParamsAndPayload();
  const {
    data: subscriptionContractData,
    isLoading: isLoadingContracts,
    refetch: getContracts,
  } = useQuery(
    [
      QUERY_KEYS.GET_CONTRACTS_KEY,
      page,
      pageSize,
      order,
      orderBy,
      dateFrom,
      dateTo,
      searchQuery,
      rest,
    ],
    () => {
      return subscriptionsServices.getContracts({
        params,
        payload,
      });
    },
    { enabled: isReady, refetchOnMount: true }
  );

  const contracts = useMemo(() => {
    return handleContracts(subscriptionContractData?.data || []);
  }, [subscriptionContractData?.data]);

  useEffect(() => {
    if (subscriptionContractData?.error) {
      showError(subscriptionContractData.error);
    }
  }, [subscriptionContractData?.error]);

  return {
    total: subscriptionContractData?.meta?.total,
    page,
    enums: enumsData?.data || {},
    pageSize: parseInt(pageSize, 10) || CONTRACTS_PAGE_SIZE,
    isLoading: isLoadingContracts,
    contracts,
    params,
    payload,
    getContracts,
  };
};
